import React, { useCallback, useMemo } from "react";

import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import { trialUnsubscriptionsQuery } from "./offerAnalytics.queries";
import {
  getTrialUnsubscriptionHref,
  trialUnsubscriptionColumns,
} from "./tableParams/trialUnsubscriptionColumns";
import useAbility from "../../../hooks/useAbility";
import DataTable from "../../dataTable/DataTable";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import NotFilterableByOfferHint from "../charts/NotFilterableByOfferHint";

import type { JSX } from "react";

import type { OfferAnalyticsFilterValues } from "./offerAnalytics.types";
import type { TrialUnsubscriptionRow } from "./tableParams/trialUnsubscriptionColumns.types";
import type { AppState } from "../../../reducers";

interface UnsubscriptionsTrialPeriodTableProps {
  filters: OfferAnalyticsFilterValues;
}

function UnsubscriptionsTrialPeriodTable({
  filters,
}: UnsubscriptionsTrialPeriodTableProps): JSX.Element {
  const { projectSlug } = useParams<{ projectSlug: string }>();
  const { t } = useTranslation("editor-analytics");
  const orgaOptions = useSelector(
    (state: AppState) =>
      state.fetchReducer.orga_config.data?.organization_options,
  );
  const { canAccess } = useAbility();
  const canAccessUsers = canAccess(orgaOptions).project.usersAnalytics;

  const {
    data: offersTrialUnsubscriptions,
    isLoading,
    isPreviousData,
    isError,
    refetch,
  } = useQuery(trialUnsubscriptionsQuery(projectSlug, filters));

  const rowList = useMemo<TrialUnsubscriptionRow[]>(
    () =>
      (offersTrialUnsubscriptions?.data ?? []).map((line) => ({
        name: `${line.first_name} ${line.last_name}`,
        email: line.email,
        subscriptionDate: line.subscription_date,
        unsubscriptionDate: line.unsubscription_date,
        trialPeriodDuration: line.offer_plan_trial_duration_sec,
        subscriptionTrialPeriodDuration: line.subscription_trial_duration_sec,
        user_uuid: line.user_uuid,
      })),
    [offersTrialUnsubscriptions?.data],
  );

  const columnList = useMemo(
    () => trialUnsubscriptionColumns({ t, projectSlug, canAccessUsers }),
    [t, projectSlug, canAccessUsers],
  );

  const getRowHref = useCallback(
    (row: TrialUnsubscriptionRow): string | undefined =>
      getTrialUnsubscriptionHref(row, projectSlug, canAccessUsers),
    [canAccessUsers, projectSlug],
  );

  const description = filters.offerUuid ? (
    <NotFilterableByOfferHint />
  ) : undefined;

  return (
    <Card className="gap-4">
      <CardHeader>
        <CardTitle>{t("unsubscriptionTrialPeriodTable")}</CardTitle>
      </CardHeader>
      <CardContent>
        <DataTable
          columns={columnList}
          data={rowList}
          defaultSorting={{ id: "subscriptionDate", desc: true }}
          description={description}
          getRowHref={getRowHref}
          isError={isError}
          isLoading={isLoading || isPreviousData}
          onRetry={refetch}
          urlSync={{ prefix: "trialUnsubscriptions" }}
          resetPageOn={[
            filters.startDate.valueOf(),
            filters.endDate.valueOf(),
            filters.offerUuid,
          ]}
        />
      </CardContent>
    </Card>
  );
}

export default UnsubscriptionsTrialPeriodTable;
